import { useEffect, useMemo, useState } from 'react'
import { useSettingsData } from '../hooks/useSettingsData.js'
import { useRecipesData } from '../hooks/useRecipesData.js'
import { formatMoney, formatQty } from '../lib/format.js'
import ComplianceDocsSection from './ComplianceDocsSection.jsx'
import DemoDataSection from './DemoDataSection.jsx'
import NotificationsSection from './NotificationsSection.jsx'
import SquareConnectSection from './SquareConnectSection.jsx'
import SubscriptionCard from './SubscriptionCard.jsx'
import AiUsageCard from './AiUsageCard.jsx'
import { useIsOperator } from '../hooks/useOperator.js'
import ToastConnectSection from './ToastConnectSection.jsx'
import ItemEditModal from './ItemEditModal.jsx'
import ItemsImportModal from './ItemsImportModal.jsx'
import ItemSalesImportModal from './ItemSalesImportModal.jsx'
import RecipeBuildModal from './RecipeBuildModal.jsx'
import RecipeLinkModal from './RecipeLinkModal.jsx'
import SalesImportModal from './SalesImportModal.jsx'
import { Skeleton, SkeletonCard, SkeletonList } from './Skeleton.jsx'
import StaffEditModal from './StaffEditModal.jsx'
import StaffImportModal from './StaffImportModal.jsx'
import SupplierEditModal from './SupplierEditModal.jsx'
import Toast from './Toast.jsx'
import { supabase } from '../lib/supabase.js'
import { useSession } from '../hooks/useSession.js'

// Settings — owner-facing admin screen.
//
// Sections:
//   1. Restaurant — name, daily target, large-entry threshold, timezone
//   1b. Sales data — daily totals imported from POS CSV
//   2. Items & Pars — catalog with search, per-item edit modal
//   2b. Recipes — AI-built menu items priced from item costs
//   3. Suppliers — add + edit; staff sub-section beneath
//   4. Account — sign-out
//
// Designed so a non-technical owner can run the kitchen without ever
// touching SQL: set pars on Toast-imported items, edit cost overrides,
// deactivate seed items, add a new supplier when one shows up.
export default function SettingsScreen() {
  const {
    restaurant,
    items,
    activeItems,
    inactiveItems,
    suppliers,
    unmatchedSuppliers,
    staff,
    salesSummary,
    loading,
    updateRestaurant,
    updateItem,
    createItem,
    importItemsFromCsv,
    updateSupplier,
    createSupplier,
    promoteSupplier,
    updateStaff,
    createStaff,
    importStaffFromCsv,
    importDailySales,
    deleteDailySales,
    deleteAllDailySales,
    itemSalesSummary,
    importItemSales,
    latestBaseline,
    // setInventoryBaseline is still exported by the hook for future
    // callers, but the manual button lives in StocktakeScreen now.
  } = useSettingsData()

  // Recipes — loaded independently from useRecipesData. Has its own
  // RLS-scoped fetch and createRecipe / deleteRecipe helpers. Kept
  // separate from useSettingsData so the recipes query doesn't slow
  // down the rest of the Settings screen on first paint.
  const {
    recipes,
    loading: recipesLoading,
    createRecipe,
    deleteRecipe,
    updateRecipeItemId,
  } = useRecipesData()

  const [editingItem, setEditingItem] = useState(null)
  const [itemCreateOpen, setItemCreateOpen] = useState(false)
  const [itemsImportOpen, setItemsImportOpen] = useState(false)
  const [editingSupplier, setEditingSupplier] = useState(null)
  const [supplierCreateOpen, setSupplierCreateOpen] = useState(false)
  const [editingStaff, setEditingStaff] = useState(null)
  const [staffCreateOpen, setStaffCreateOpen] = useState(false)
  const [staffImportOpen, setStaffImportOpen] = useState(false)
  const [signingOut, setSigningOut] = useState(false)
  const { user } = useSession()

  async function handleSignOut() {
    if (signingOut) return
    setSigningOut(true)
    try {
      await supabase.auth.signOut()
      // App.jsx's session listener flips to LoginScreen automatically.
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[SettingsScreen] sign out failed', e)
      setToast('Could not sign out. Try again.')
      setSigningOut(false)
    }
  }
  const [recipeBuildOpen, setRecipeBuildOpen] = useState(false)
  const [linkingRecipeId, setLinkingRecipeId] = useState(null)
  // Track which recipe row is mid-delete so we can grey it out.
  const [removingRecipeId, setRemovingRecipeId] = useState(null)
  const [salesImportOpen, setSalesImportOpen] = useState(false)
  const [itemSalesImportOpen, setItemSalesImportOpen] = useState(false)
  // Track which sales row is mid-delete so we can grey it out + disable
  // the × button (prevents double-clicks).
  const [removingSalesDate, setRemovingSalesDate] = useState(null)
  const [clearingAllSales, setClearingAllSales] = useState(false)
  const [showInactive, setShowInactive] = useState(false)
  const [itemSearch, setItemSearch] = useState('')
  // AI usage card is back-office only — operators see their per-feature
  // spend for trial-abuse detection, customers don't see what their
  // own AI usage costs. Same gate as the Operator Queue button.
  const { isOperator } = useIsOperator()
  // Per-recipe expand state — Set of recipe ids currently shown with
  // their component breakdown. Click row to toggle.
  const [expandedRecipeIds, setExpandedRecipeIds] = useState(() => new Set())
  function toggleRecipeExpand(id) {
    setExpandedRecipeIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }
  // Items section collapses by default once the catalog is large so it
  // doesn't dominate the Settings page. Friend at I Smell Bacon flagged
  // this 2026-05-04 — too much scrolling past inventory to reach
  // recipes / suppliers / staff. Persist per-restaurant in localStorage
  // so the manager's preference sticks across reloads.
  const itemsCollapseKey = `settings_items_collapsed_${restaurant?.id || 'none'}`
  const [itemsCollapsed, setItemsCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false
    const stored = window.localStorage.getItem(itemsCollapseKey)
    return stored === null ? false : stored === '1'
  })
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(itemsCollapseKey, itemsCollapsed ? '1' : '0')
    }
  }, [itemsCollapsed, itemsCollapseKey])
  const [toast, setToast] = useState('')
  const [promotingName, setPromotingName] = useState(null)

  // Restaurant form local state — committed to DB only on Save click
  // so the manager can mash numbers without firing a RPC every keystroke.
  const [restName, setRestName] = useState('')
  const [restDailyTarget, setRestDailyTarget] = useState('')
  const [restLargeThreshold, setRestLargeThreshold] = useState('')
  const [restSaving, setRestSaving] = useState(false)

  // Hydrate the restaurant form state when the data first lands or
  // when we switch restaurants. Keyed on restaurant.id so manager
  // edits aren't clobbered by background refetches of the same row.
  useEffect(() => {
    if (restaurant) {
      setRestName(restaurant.name ?? '')
      setRestDailyTarget(String(restaurant.daily_target ?? '50'))
      setRestLargeThreshold(String(restaurant.large_entry_threshold ?? '50'))
    }
  }, [restaurant?.id])  // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Filtered items -------------------------------
  // NOTE: Hooks must run on every render — declared BEFORE any
  // conditional early return (Rules of Hooks).
  const visibleItems = useMemo(() => {
    const q = itemSearch.trim().toLowerCase()
    const base = showInactive ? items : activeItems
    if (!q) return base
    return base.filter((i) =>
      i.name.toLowerCase().includes(q) ||
      (i.category || '').toLowerCase().includes(q)
    )
  }, [items, activeItems, showInactive, itemSearch])

  // ---------- Loading skeleton ------------------------------
  if (loading && !restaurant) {
    return (
      <div className="min-h-full bg-white pb-12">
        <main className="mx-auto max-w-screen-sm">
          <SkeletonCard />
          <section className="mx-4 mt-6">
            <Skeleton className="h-4 w-32" />
            <SkeletonList rows={5} className="mt-2" />
          </section>
          <section className="mx-4 mt-6">
            <Skeleton className="h-4 w-32" />
            <SkeletonList rows={3} className="mt-2" />
          </section>
        </main>
      </div>
    )
  }

  // ---------- Handlers --------------------------------------
  async function saveRestaurant() {
    if (!restaurant || restSaving) return
    setRestSaving(true)
    try {
      await updateRestaurant({
        name:                  restName.trim() || restaurant.name,
        daily_target:          Number(restDailyTarget),
        large_entry_threshold: Number(restLargeThreshold),
      })
      setToast('Restaurant settings saved.')
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[SettingsScreen] save restaurant', e)
      setToast('Could not save restaurant settings.')
    } finally {
      setRestSaving(false)
    }
  }

  async function handleSaveItem(itemId, patch) {
    await updateItem(itemId, patch)
    setToast('Item saved.')
  }

  async function handleCreateItem(payload) {
    await createItem(payload)
    setToast(`Added ${payload.name.trim()}.`)
  }

  async function handleImportItems(rows, options) {
    const { inserted, updated, skipped } = await importItemsFromCsv(rows, options)
    const parts = []
    if (inserted > 0) parts.push(`${inserted} added`)
    if (updated  > 0) parts.push(`${updated} updated`)
    if (skipped  > 0) parts.push(`${skipped} skipped`)
    setToast(parts.length > 0 ? parts.join(' \u00b7 ') : 'No changes — everything was already up to date.')
  }

  async function handleSaveSupplier(id, patch) {
    await updateSupplier(id, patch)
    setToast('Supplier saved.')
  }

  async function handleCreateSupplier(payload) {
    await createSupplier(payload)
    setToast(`Added supplier "${payload.name.trim()}".`)
  }

  async function handlePromoteSupplier(name) {
    if (promotingName) return
    setPromotingName(name)
    try {
      await promoteSupplier(name)
      setToast(`Added "${name}" to your supplier catalog.`)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[handlePromoteSupplier]', e)
      setToast('Could not add supplier. Try again.')
    } finally {
      setPromotingName(null)
    }
  }

  async function handleSaveStaff(id, patch) {
    await updateStaff(id, patch)
    setToast('Staff saved.')
  }

  async function handleCreateStaff(payload) {
    await createStaff(payload)
    setToast(`Added ${payload.name.trim()}.`)
  }

  async function handleImportStaff(rows) {
    const { inserted, updated, skipped } = await importStaffFromCsv(rows)
    const parts = []
    if (inserted > 0) parts.push(`${inserted} added`)
    if (updated  > 0) parts.push(`${updated} updated`)
    if (skipped  > 0) parts.push(`${skipped} skipped`)
    setToast(parts.length > 0 ? parts.join(' \u00b7 ') : 'No changes — everyone was already up to date.')
  }

  async function handleImportSales(rows) {
    const count = await importDailySales(rows)
    setToast(`Imported ${count} ${count === 1 ? 'day' : 'days'} of sales.`)
  }

  async function handleRemoveSalesDate(saleDate) {
    if (removingSalesDate) return
    setRemovingSalesDate(saleDate)
    try {
      await deleteDailySales(saleDate)
      setToast(`Removed sales for ${saleDate}.`)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[handleRemoveSalesDate]', e)
      setToast('Could not remove. Try again.')
    } finally {
      setRemovingSalesDate(null)
    }
  }

  async function handleImportItemSales(payload) {
    const { inserted, updated, unmatched } = await importItemSales(payload)
    const parts = []
    if (inserted > 0) parts.push(`${inserted} added`)
    if (updated  > 0) parts.push(`${updated} updated`)
    if (unmatched > 0) parts.push(`${unmatched} unmatched`)
    setToast(parts.length > 0 ? parts.join(' \u00b7 ') : 'No new item-sales rows.')
  }


  async function handleDeleteRecipe(recipe) {
    if (removingRecipeId) return
    const ok = window.confirm(
      `Delete recipe "${recipe.name}"?\n\nThis removes the recipe and all its components. Your inventory items aren't affected.`
    )
    if (!ok) return
    setRemovingRecipeId(recipe.id)
    try {
      await deleteRecipe(recipe.id)
      setToast(`Deleted "${recipe.name}".`)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[handleDeleteRecipe]', e)
      setToast('Could not delete recipe. Try again.')
    } finally {
      setRemovingRecipeId(null)
    }
  }

  async function handleClearAllSales() {
    if (clearingAllSales) return
    const days = salesSummary?.row_count ?? 0
    const ok = window.confirm(
      `Remove all imported sales data?\n\nThis deletes ${days} ${days === 1 ? 'day' : 'days'} of sales totals from the app. Your POS records are not affected — you can always re-export and re-upload.\n\nContinue?`
    )
    if (!ok) return
    setClearingAllSales(true)
    try {
      await deleteAllDailySales()
      setToast('All imported sales cleared.')
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[handleClearAllSales]', e)
      setToast('Could not clear sales data. Try again.')
    } finally {
      setClearingAllSales(false)
    }
  }

  return (
    <div className="min-h-full bg-white pb-12">
      <main className="mx-auto max-w-screen-sm">

        {/* ---------- 0. PLAN & BILLING ----------------------- */}
        {/* Sits at the very top so trial countdown + manage-billing
            are the first things a customer sees. The card auto-hides
            its CTAs based on subscription state, so it's lightweight
            for active customers and prominent for trialing ones. */}
        <SubscriptionCard />

        {/* AI usage card is back-office only — operators see their
            per-feature spend for trial-abuse detection and overall
            cost monitoring. Customers don't see what their own AI
            usage costs (would invite "why does this cost X?" questions
            and isn't part of the value prop they're paying for). */}
        {isOperator && <AiUsageCard />}

        {/* ---------- 1. RESTAURANT --------------------------- */}
        <section
          className="mx-4 mt-4 rounded-2xl border border-black/10 bg-white p-5 shadow-sm"
          aria-label="Restaurant settings"
        >
          <div className="text-xs font-medium text-black/55">
            Restaurant
          </div>

          <div className="mt-3 space-y-3">
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="rest-name">Name</label>
              <input
                id="rest-name"
                type="text"
                value={restName ?? ''}
                onChange={(e) => setRestName(e.target.value)}
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="rest-target">
                  Daily waste target
                </label>
                <input
                  id="rest-target"
                  type="number"
                  inputMode="decimal"
                  step="1"
                  min="0"
                  value={restDailyTarget ?? ''}
                  onChange={(e) => setRestDailyTarget(e.target.value)}
                  className="w-full rounded-lg border border-black/15 px-4 py-3 text-base font-medium tabular-nums outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
                />
                <p className="mt-1 text-xs text-black/50">
                  Goal for total waste $ per day. Today's running total turns red above this.
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="rest-threshold">
                  Large-entry alert
                </label>
                <input
                  id="rest-threshold"
                  type="number"
                  inputMode="decimal"
                  step="1"
                  min="0"
                  value={restLargeThreshold ?? ''}
                  onChange={(e) => setRestLargeThreshold(e.target.value)}
                  className="w-full rounded-lg border border-black/15 px-4 py-3 text-base font-medium tabular-nums outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
                />
                <p className="mt-1 text-xs text-black/50">
                  Single waste entry above this fires the orange "owner notified" banner.
                </p>
              </div>
            </div>

            <div className="text-xs text-black/55">
              Timezone: <span className="font-medium text-black/80">{restaurant?.timezone}</span>
              {restaurant?.toast_restaurant_guid && (
                <> · Toast connected: <span className="font-medium text-emerald-700">yes</span></>
              )}
            </div>

            <button
              type="button"
              onClick={saveRestaurant}
              disabled={restSaving}
              className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-3 text-base font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {restSaving ? 'Saving…' : 'Save restaurant settings'}
            </button>
          </div>
        </section>

        {/* ---------- 1b. SALES DATA -------------------------- */}
        {/* Daily sales totals — uploaded as a CSV from the POS.
            Powers waste-as-%-of-sales on the Huddle. POS-agnostic
            (works for Toast, Square, Clover, Lightspeed exports). */}
        <section
          className="mx-4 mt-4 rounded-2xl border border-black/10 bg-white p-5 shadow-sm"
          aria-label="Sales data"
        >
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-xs font-medium text-black/55">
              Sales data
            </div>
            <button
              type="button"
              onClick={() => setSalesImportOpen(true)}
              className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            >
              + Import CSV
            </button>
          </div>

          {salesSummary?.row_count > 0 ? (
            <>
              <div className="mt-2 text-xs text-black/55">
                <span className="font-medium tabular-nums text-black/80">
                  {salesSummary.row_count}
                </span>{' '}
                {salesSummary.row_count === 1 ? 'day' : 'days'} imported,{' '}
                <span className="font-medium tabular-nums">{salesSummary.first_date}</span> to{' '}
                <span className="font-medium tabular-nums">{salesSummary.last_date}</span>
              </div>

              {/* Rollups: today / 7-day / 30-day */}
              <div className="mt-3 grid grid-cols-3 gap-2">
                <RollupTile
                  label="Today"
                  value={salesSummary.today_net}
                  hint={salesSummary.today_tx > 0 ? `${salesSummary.today_tx} orders` : null}
                  emphasized
                />
                <RollupTile
                  label="Last 7 days"
                  value={salesSummary.week_net}
                  hint={salesSummary.week_tx > 0 ? `${salesSummary.week_tx} orders` : null}
                />
                <RollupTile
                  label="Last 30 days"
                  value={salesSummary.month_net}
                  hint={salesSummary.month_tx > 0 ? `${salesSummary.month_tx} orders` : null}
                />
              </div>

              {/* Recent days list */}
              {salesSummary.recent_days.length > 0 && (
                <div className="mt-4">
                  <div className="mb-1 text-xs font-medium text-black/55">
                    Most recent
                  </div>
                  <ul className="overflow-hidden rounded-xl border border-black/10">
                    {salesSummary.recent_days.map((d) => {
                      const isRemoving = removingSalesDate === d.sale_date
                      return (
                        <li
                          key={d.sale_date}
                          className={`flex items-center justify-between gap-2 border-b border-black/5 px-3 py-2 text-sm last:border-b-0 ${isRemoving ? 'opacity-50' : ''}`}
                        >
                          <div className="min-w-0 flex-1 tabular-nums">
                            <span className="font-medium">{d.sale_date}</span>
                            {d.transaction_count != null && (
                              <span className="ml-2 text-xs text-black/55">
                                {d.transaction_count} {d.transaction_count === 1 ? 'order' : 'orders'}
                              </span>
                            )}
                          </div>
                          <div className="text-right tabular-nums">
                            <span className="font-medium">
                              {formatMoney(Number(d.net_sales || 0))}
                            </span>
                            {Number(d.gross_sales || 0) > Number(d.net_sales || 0) && (
                              <span className="ml-2 text-xs text-black/55">
                                {formatMoney(Number(d.gross_sales))} gross
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveSalesDate(d.sale_date)}
                            disabled={isRemoving || clearingAllSales}
                            aria-label={`Remove sales for ${d.sale_date}`}
                            title="Remove this day"
                            className="min-h-[36px] min-w-[36px] rounded-lg px-2 text-black/40 hover:bg-red-50 hover:text-red-600 active:bg-red-100 disabled:cursor-wait disabled:opacity-50"
                          >
                            {isRemoving ? '…' : '✕'}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}

              <div className="mt-3 flex items-center justify-between gap-2">
                <p className="text-xs text-black/55">
                  Net sales shown. Re-export and re-upload to refresh — overlapping
                  dates overwrite cleanly.
                </p>
                <button
                  type="button"
                  onClick={handleClearAllSales}
                  disabled={clearingAllSales || removingSalesDate != null}
                  className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 active:bg-red-100 disabled:cursor-wait disabled:opacity-50"
                >
                  {clearingAllSales ? 'Clearing…' : 'Clear all'}
                </button>
              </div>
            </>
          ) : (
            <p className="mt-3 text-sm leading-relaxed text-black/65">
              Import a sales CSV from your POS (Toast Sales Summary export,
              Square daily sales, etc.) to unlock waste-as-%-of-sales on the
              Huddle. Doesn't require any paid POS API access — just a
              standard report export.
            </p>
          )}
        </section>

        {/* ---------- 1c. ITEM SALES + BASELINE -------------- */}
        {/* Two related controls: per-item sales (Toast Item Sales CSV)
            and the inventory baseline snapshot. Both feed the upcoming
            Theoretical-vs-Actual variance report. Item sales are the
            theoretical side (what the recipes SHOULD have used);
            baseline + current_on_hand are the actual side. */}
        <section
          className="mx-4 mt-4 rounded-2xl border border-black/10 bg-white p-5 shadow-sm"
          aria-label="Item sales and inventory baseline"
        >
          <div className="text-xs font-medium text-black/55">
            Item sales &amp; inventory baseline
          </div>
          <p className="mt-1 text-xs text-black/60 leading-snug">
            Foundation for the upcoming variance report — measures
            shrinkage by comparing what your recipes should have used
            against what actually moved.
          </p>

          {/* Item sales card */}
          <div className="mt-3 rounded-xl border border-black/10 bg-black/[0.02] p-3">
            <div className="flex items-baseline justify-between gap-2">
              <div className="text-sm font-medium">Item sales</div>
              <button
                type="button"
                onClick={() => setItemSalesImportOpen(true)}
                className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
              >
                + Import CSV
              </button>
            </div>
            {itemSalesSummary?.row_count > 0 ? (
              <div className="mt-2 text-xs text-black/65 leading-snug">
                <span className="font-medium tabular-nums text-black/85">
                  {itemSalesSummary.row_count}
                </span>{' '}
                rows imported,{' '}
                <span className="tabular-nums">{itemSalesSummary.first_period_start}</span>{' '}
                to{' '}
                <span className="tabular-nums">{itemSalesSummary.last_period_end}</span>
                {' \u00b7 '}
                <span className="font-medium text-emerald-700 tabular-nums">
                  {itemSalesSummary.matched_count}
                </span>
                {' matched'}
                {itemSalesSummary.unmatched_count > 0 && (
                  <>
                    {' \u00b7 '}
                    <span className="font-medium text-amber-700 tabular-nums">
                      {itemSalesSummary.unmatched_count}
                    </span>
                    {' unmatched'}
                  </>
                )}
              </div>
            ) : (
              <p className="mt-2 text-xs text-black/55 leading-snug">
                No item-sales data yet. Toast → Reports → Item Sales →
                by Item → Export CSV.
              </p>
            )}
          </div>

          {/* Baseline status — read-only. The "Set baseline" button
              that used to live here was replaced by the Stocktake
              tab: completing a stocktake auto-snapshots each item
              with source='stocktake' and that becomes the next
              variance period's baseline. We still surface the most-
              recent baseline timestamp here so the manager can see
              it without bouncing tabs. */}
          <div className="mt-3 rounded-xl border border-black/10 bg-black/[0.02] p-3">
            <div className="text-sm font-medium">Inventory baseline</div>
            {latestBaseline ? (
              <div className="mt-2 text-xs text-black/65 leading-snug">
                Last snapshot:{' '}
                <span className="font-medium tabular-nums">
                  {new Date(latestBaseline.snapshot_at).toLocaleString()}
                </span>
                <div className="mt-1 text-black/55">
                  Run a stocktake from the <span className="font-medium">Stocktake</span> tab
                  to refresh the baseline.
                </div>
              </div>
            ) : (
              <p className="mt-2 text-xs text-black/55 leading-snug">
                No baseline yet. Run a stocktake from the
                <span className="font-medium"> Stocktake </span> tab — completing one
                snapshots every counted item and starts the variance clock.
              </p>
            )}
          </div>
        </section>

        {/* ---------- 2. ITEMS & PARS ------------------------- */}
        <section
          className="mx-4 mt-6"
          aria-label="Items and pars"
        >
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <button
              type="button"
              onClick={() => setItemsCollapsed(c => !c)}
              className="flex items-baseline gap-2 text-left"
              aria-expanded={!itemsCollapsed}
              aria-controls="settings-items-body"
            >
              <span aria-hidden="true" className="text-black/55 text-sm leading-none">
                {itemsCollapsed ? '▸' : '▾'}
              </span>
              <h2 className="text-base font-medium">
                Items &amp; Pars
                <span className="ml-2 text-xs font-normal text-black/55">
                  {activeItems.length} active{inactiveItems.length > 0 && ` · ${inactiveItems.length} inactive`}
                </span>
              </h2>
            </button>
            <div className="flex items-center gap-2">
              {!itemsCollapsed && (
                <label className="text-xs flex items-center gap-1 text-black/60">
                  <input
                    type="checkbox"
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    className="h-3.5 w-3.5"
                  />
                  Show inactive
                </label>
              )}
              <button
                type="button"
                onClick={() => setItemsImportOpen(true)}
                className="rounded-lg border border-blue-600 bg-white px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-50"
              >
                Import POS
              </button>
              <button
                type="button"
                onClick={() => setItemCreateOpen(true)}
                className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
              >
                + Add item
              </button>
            </div>
          </div>

          {!itemsCollapsed && (
          <div id="settings-items-body">
          <input
            type="search"
            value={itemSearch}
            onChange={(e) => setItemSearch(e.target.value)}
            placeholder="Search items by name or category…"
            className="mb-2 w-full rounded-lg border border-black/15 px-4 py-2 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
          />

          {visibleItems.length === 0 ? (
            <div className="rounded-xl border border-dashed border-black/15 px-4 py-6 text-center text-sm text-black/60">
              No matching items.
            </div>
          ) : (
            <ul className="overflow-hidden rounded-xl border border-black/10">
              {visibleItems.map((i) => (
                <li key={i.id}>
                  <button
                    type="button"
                    onClick={() => setEditingItem(i)}
                    className={`flex w-full items-start justify-between gap-3 border-b border-black/5 px-4 py-3 text-left last:border-b-0 hover:bg-blue-50 ${
                      i.active ? '' : 'opacity-50'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-base font-medium">
                        {i.name}
                        {i.toast_guid && (
                          <span className="ml-2 inline-block rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                            Toast
                          </span>
                        )}
                        {!i.active && (
                          <span className="ml-2 inline-block rounded bg-black/10 px-1.5 py-0.5 text-[10px] font-medium text-black/55">
                            inactive
                          </span>
                        )}
                      </div>
                      <div className="truncate text-xs text-black/60">
                        Par {formatQty(i.par_qty, i.unit)} ·
                        On hand {formatQty(i.current_on_hand, i.unit)} ·
                        {' '}{formatMoney(i.cost_per_unit)}/{i.unit}
                        {Number(i.pack_size) > 1 && i.pack_unit && Number(i.cost_per_unit) > 0 && (
                          <>
                            {' '}<span className="text-emerald-700 font-medium">
                              · {formatMoney(Number(i.cost_per_unit) / Number(i.pack_size))}/{i.pack_unit}
                            </span>
                          </>
                        )}
                        {i.category ? ` · ${i.category}` : ''}
                      </div>
                    </div>
                    <span className="text-blue-600 text-sm font-medium" aria-hidden="true">Edit ›</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          </div>
          )}
        </section>

        {/* ---------- 2b. RECIPES ----------------------------- */}
        {/* Menu-item recipes — built via the AI Recipe Builder.
            Costs are computed live from current item cost_per_unit, so
            edits to ingredient costs in the Items section flow through
            here automatically. Foundation for Theoretical-vs-Actual
            variance reporting (next AI feature). */}
        <section className="mx-4 mt-6" aria-label="Recipes">
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <h2 className="text-base font-medium">
              Recipes
              <span className="ml-2 text-xs font-normal text-black/55">
                {recipes.length} {recipes.length === 1 ? 'recipe' : 'recipes'}
              </span>
            </h2>
            <button
              type="button"
              onClick={() => setRecipeBuildOpen(true)}
              disabled={activeItems.length === 0}
              className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              title={activeItems.length === 0 ? 'Add items first' : 'AI builds the recipe from your catalog'}
            >
              + AI Build Recipe
            </button>
          </div>

          {recipesLoading && recipes.length === 0 ? (
            <SkeletonList rows={2} />
          ) : recipes.length === 0 ? (
            <div className="rounded-xl border border-dashed border-black/15 px-4 py-6 text-sm text-black/65">
              No recipes yet. Tap{' '}
              <span className="font-medium text-blue-600">+ AI Build Recipe</span>{' '}
              and describe a dish — the AI breaks it into ingredient components
              from your catalog, costs it out, and suggests a menu price.
              Foundation for the Theoretical-vs-Actual usage report coming next.
            </div>
          ) : (
            <ul className="overflow-hidden rounded-xl border border-black/10">
              {recipes.map((r) => {
                const isRemoving = removingRecipeId === r.id
                const componentCount = (r.components || []).length
                const isExpanded = expandedRecipeIds.has(r.id)
                // Sales velocity hint — strict linkage. If recipe.item_id
                // is null, surface the "not linked" warning so the
                // manager knows variance won't include this recipe.
                const last7d = r.item_id
                  ? Number(itemSalesSummary?.last7d_units_by_item_id?.[r.item_id] || 0)
                  : null
                return (
                  <li
                    key={r.id}
                    className={`border-b border-black/5 last:border-b-0 ${
                      isRemoving ? 'opacity-50' : ''
                    }`}
                  >
                  <div className="flex items-start gap-2 px-4 py-3">
                    <button
                      type="button"
                      onClick={() => toggleRecipeExpand(r.id)}
                      aria-expanded={isExpanded}
                      aria-controls={`recipe-detail-${r.id}`}
                      className="flex min-w-0 flex-1 items-start gap-2 text-left"
                    >
                      <span aria-hidden="true" className="mt-1 text-xs text-black/45 leading-none">
                        {isExpanded ? '▾' : '▸'}
                      </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-base font-medium">
                        {r.name}
                        {!r.cost_known && (
                          <span
                            className="ml-2 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
                            title="One or more components have $0 cost in your catalog"
                          >
                            cost incomplete
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-black/60">
                        {componentCount} {componentCount === 1 ? 'component' : 'components'}
                        {' · '}yields <span className="tabular-nums">{Number(r.yield_servings || 1)}</span>
                        {Number(r.target_food_cost_pct || 0) > 0 && (
                          <> · target <span className="tabular-nums">{Number(r.target_food_cost_pct)}%</span></>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs">
                        <span className="text-black/55">
                          COGS{' '}
                          <span className="font-medium tabular-nums text-black/85">
                            {formatMoney(r.cost_per_serving)}
                          </span>
                          /serving
                        </span>
                        {r.target_price > 0 && (
                          <span className="text-emerald-700">
                            Target price{' '}
                            <span className="font-medium tabular-nums">
                              {formatMoney(r.target_price)}
                            </span>
                          </span>
                        )}
                        {last7d != null && last7d > 0 && (
                          <span className="text-blue-700">
                            Last 7d:{' '}
                            <span className="font-medium tabular-nums">{last7d}</span>{' '}
                            sold
                          </span>
                        )}
                        {last7d != null && last7d === 0 && itemSalesSummary?.row_count > 0 && (
                          <span className="text-black/45">
                            No sales in last 7 days
                          </span>
                        )}
                      </div>
                    </div>
                    </button>
                    {!r.item_id && (
                      <button
                        type="button"
                        onClick={() => setLinkingRecipeId(r.id)}
                        title="Pick the menu item this recipe produces"
                        className="shrink-0 self-center rounded bg-amber-100 px-2 py-1 text-[11px] font-medium text-amber-800 hover:bg-amber-200"
                      >
                        Link to item ›
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDeleteRecipe(r)}
                      disabled={isRemoving}
                      aria-label={`Delete recipe ${r.name}`}
                      title="Delete recipe"
                      className="min-h-[36px] min-w-[36px] shrink-0 rounded-lg px-2 text-black/40 hover:bg-red-50 hover:text-red-600 active:bg-red-100 disabled:cursor-wait disabled:opacity-50"
                    >
                      {isRemoving ? '…' : '✕'}
                    </button>
                  </div>
                  {isExpanded && (
                    <div id={`recipe-detail-${r.id}`} className="border-t border-black/5 bg-black/[0.015] px-4 py-3">
                      {componentCount === 0 ? (
                        <div className="text-xs text-black/55">
                          No components on this recipe yet.
                        </div>
                      ) : (
                        <>
                          <div className="text-[10px] font-medium text-black/55">
                            Components ({componentCount})
                          </div>
                          <ul className="mt-2 divide-y divide-black/5 overflow-hidden rounded-lg border border-black/10 bg-white">
                            {(r.components || []).map((c) => {
                              const itemName = c.item?.name || '(unknown item)'
                              const baseCost = Number(c.item?.cost_per_unit || 0)
                              const packSize = Number(c.item?.pack_size || 1)
                              const usingInner = c.unit && c.item?.pack_unit && c.unit === c.item.pack_unit && packSize > 1
                              const effCost = usingInner ? baseCost / packSize : baseCost
                              const lineCost = effCost * Number(c.quantity || 0)
                              return (
                                <li key={c.id} className="flex items-baseline justify-between gap-3 px-3 py-2 text-sm">
                                  <div className="min-w-0 flex-1">
                                    <div className="truncate font-medium">{itemName}</div>
                                    <div className="text-xs text-black/55">
                                      <span className="tabular-nums">{Number(c.quantity || 0)}</span> {c.unit}
                                      {' · '}
                                      <span className="tabular-nums">{formatMoney(effCost)}</span>/{c.unit}
                                      {usingInner && <span className="ml-1 text-emerald-700">(inner)</span>}
                                    </div>
                                  </div>
                                  <div className="text-sm font-medium tabular-nums">
                                    {formatMoney(lineCost)}
                                  </div>
                                </li>
                              )
                            })}
                          </ul>
                          <div className="mt-2 flex items-baseline justify-between text-sm">
                            <span className="text-black/55">Total recipe cost</span>
                            <span className="font-medium tabular-nums">{formatMoney(r.total_cogs)}</span>
                          </div>
                          {Number(r.yield_servings || 1) > 1 && (
                            <div className="mt-0.5 flex items-baseline justify-between text-xs">
                              <span className="text-black/55">
                                ÷ {Number(r.yield_servings || 1)} servings
                              </span>
                              <span className="tabular-nums text-black/65">
                                {formatMoney(r.cost_per_serving)}/serving
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  </li>
                )
              })}
            </ul>
          )}
        </section>

        {/* ---------- 3. SUPPLIERS ---------------------------- */}
        <section className="mx-4 mt-6 mb-2" aria-label="Suppliers">
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <h2 className="text-base font-medium">
              Suppliers
              <span className="ml-2 text-xs font-normal text-black/55">
                {suppliers.filter((s) => s.active).length} active
              </span>
            </h2>
            <button
              type="button"
              onClick={() => setSupplierCreateOpen(true)}
              className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            >
              + Add supplier
            </button>
          </div>

          {suppliers.length === 0 ? (
            <div className="rounded-xl border border-dashed border-black/15 px-4 py-6 text-center text-sm text-black/60">
              No suppliers yet. Tap{' '}
              <span className="font-medium text-blue-600">+ Add supplier</span>.
            </div>
          ) : (
            <ul className="overflow-hidden rounded-xl border border-black/10">
              {suppliers.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => setEditingSupplier(s)}
                    className={`flex w-full items-start justify-between gap-3 border-b border-black/5 px-4 py-3 text-left last:border-b-0 hover:bg-blue-50 ${
                      s.active ? '' : 'opacity-50'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-base font-medium">
                        {s.name}
                        {!s.active && (
                          <span className="ml-2 inline-block rounded bg-black/10 px-1.5 py-0.5 text-[10px] font-medium text-black/55">
                            inactive
                          </span>
                        )}
                      </div>
                      {(s.contact || s.notes) && (
                        <div className="truncate text-xs text-black/60">
                          {[s.contact, s.notes].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </div>
                    <span className="text-blue-600 text-sm font-medium" aria-hidden="true">Edit ›</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Staff section — managed here for consistency with how
              suppliers and items are administered. The Daily Huddle
              shows today's roster + labor cost using this same data. */}
          <div className="mt-6">
            <div className="mb-2 flex items-baseline justify-between gap-2">
              <h2 className="text-base font-medium">
                Staff
                <span className="ml-2 text-xs font-normal text-black/55">
                  {staff.filter((s) => s.active).length} active
                </span>
              </h2>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setStaffImportOpen(true)}
                  className="rounded-lg border border-blue-600 bg-white px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-50"
                >
                  Import CSV
                </button>
                <button
                  type="button"
                  onClick={() => setStaffCreateOpen(true)}
                  className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
                >
                  + Add staff
                </button>
              </div>
            </div>

            {staff.length === 0 ? (
              <div className="rounded-xl border border-dashed border-black/15 px-4 py-6 text-center text-sm text-black/60">
                No staff yet. Tap{' '}
                <span className="font-medium text-blue-600">+ Add staff</span>{' '}
                to add your team — name, role, and hourly wage. Once added,
                you can mark who's working today on the Huddle screen.
              </div>
            ) : (
              <ul className="overflow-hidden rounded-xl border border-black/10">
                {staff.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => setEditingStaff(s)}
                      className={`flex w-full items-start justify-between gap-3 border-b border-black/5 px-4 py-3 text-left last:border-b-0 hover:bg-blue-50 ${
                        s.active ? '' : 'opacity-50'
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-base font-medium">
                          {s.name}
                          {!s.active && (
                            <span className="ml-2 inline-block rounded bg-black/10 px-1.5 py-0.5 text-[10px] font-medium text-black/55">
                              inactive
                            </span>
                          )}
                        </div>
                        <div className="truncate text-xs text-black/60">
                          {[
                            s.role,
                            s.hourly_wage != null ? `${formatMoney(s.hourly_wage)}/hr` : null,
                            s.phone,
                          ].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      <span className="text-blue-600 text-sm font-medium" aria-hidden="true">Edit ›</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Suggested suppliers — free-text names from receive_logs
              that aren't in the catalog yet. One-tap promote backfills
              the matching receive_logs to link them. Hidden when empty. */}
          {unmatchedSuppliers.length > 0 && (
            <div className="mt-4">
              <div className="mb-1 text-xs font-medium text-black/55">
                Suggested from your receivings
              </div>
              <p className="mb-2 text-xs text-black/55">
                Names you've typed into the Receive form that aren't in your catalog yet.
                Adding them links existing deliveries automatically.
              </p>
              <ul className="overflow-hidden rounded-xl border border-amber-200 bg-amber-50/40">
                {unmatchedSuppliers.map((s) => {
                  const isPromoting = promotingName === s.name
                  return (
                    <li
                      key={s.name}
                      className={`flex items-center justify-between gap-3 border-b border-amber-100 px-4 py-3 last:border-b-0 ${
                        isPromoting ? 'opacity-50' : ''
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-base font-medium">{s.name}</div>
                        <div className="text-xs text-black/55">
                          {s.receiving_count} {s.receiving_count === 1 ? 'delivery' : 'deliveries'} logged
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handlePromoteSupplier(s.name)}
                        disabled={isPromoting}
                        className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 active:bg-blue-800 disabled:cursor-wait disabled:opacity-50"
                      >
                        {isPromoting ? '…' : '+ Add to catalog'}
                      </button>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
        </section>

        {/* ---------- 3b. COMPLIANCE DOCUMENTS --------------- */}
        {/* Liquor license, food handler certs, insurance — anything
            that quietly becomes a $5-50k problem when it lapses. */}
        <div className="mx-4">
          <ComplianceDocsSection />
        </div>

        {/* ---------- 3c. SQUARE POS CONNECT ----------------- */}
        {/* Auto-sync items + sales from the merchant's Square POS.
            Replaces CSV import for any restaurant on Square. Token
            stored in restaurants.square_access_token (RLS-protected);
            actual API calls happen server-side via the square-sync
            edge function. */}
        {restaurant && (
          <div className="mx-4">
            {/* key on the unit id so switching the selected unit remounts
                this section and discards stale locations[]/pickerOpen
                state from the previous unit (prevents a unit B location
                being POSTed against unit A's restaurant_id). Single-unit
                accounts have a stable concrete id here, so no remount churn. */}
            <SquareConnectSection
              key={restaurant?.id ?? 'none'}
              restaurant={restaurant}
              onChanged={() => { window.location.reload() }}
            />
          </div>
        )}

        {/* ---------- 3d. TOAST POS CONNECT ------------------ */}
        {/* Same idea as Square but for Toast merchants. Toast uses
            machine-to-machine partner auth (no per-merchant consent
            screen) — customer enters their Toast Restaurant GUID
            and we authenticate via partner credentials server-side. */}
        {restaurant && (
          <div className="mx-4">
            {/* Same per-unit stale-state risk as Square: this section holds
                a typed `guid` input plus error/info local state. Remount on
                unit switch so a GUID typed for one unit can't be saved
                against another. Stable id for single-unit accounts. */}
            <ToastConnectSection
              key={restaurant?.id ?? 'none'}
              restaurant={restaurant}
              onChanged={() => { window.location.reload() }}
            />
          </div>
        )}

        {/* ---------- 3d. DEMO DATA -------------------------- */}
        {/* New tenants land in an empty app — this lets them load a
            small BBQ-joint dataset (items + recipes + 14d sales) so
            every screen has something to show. Clearable any time. */}
        {isOperator && restaurant?.id && (
          <div className="mx-4">
            <DemoDataSection
              restaurantId={restaurant.id}
              onChanged={() => { window.location.reload() }}
            />
          </div>
        )}

        {/* ---------- 3e. PUSH NOTIFICATIONS (operator-only) ----- */}
        {/* Web push opt-in + manual test push (v1). Operator-gated,
            never customer-facing — sends ONLY a test notification. */}
        {isOperator && (
          <div className="mx-4">
            <NotificationsSection />
          </div>
        )}

        {/* ---------- 4. ACCOUNT ----------------------------- */}
        {user && (
          <section
            className="mx-4 mt-6 mb-2 rounded-2xl border border-black/10 bg-white p-5 shadow-sm"
            aria-label="Account"
          >
            <div className="text-xs font-medium text-black/55">
              Account
            </div>
            <div className="mt-2 text-sm text-black/75">
              Signed in as{' '}
              <span className="font-medium text-black">{user.email}</span>
            </div>
            <button
              type="button"
              onClick={handleSignOut}
              disabled={signingOut}
              className="mt-3 min-h-btn w-full rounded-lg border border-red-200 bg-white px-4 py-3 text-base font-medium text-red-600 hover:bg-red-50 active:bg-red-100 disabled:cursor-wait disabled:opacity-50"
            >
              {signingOut ? 'Signing out\u2026' : 'Sign out'}
            </button>
          </section>
        )}
      </main>

      <ItemEditModal
        open={!!editingItem}
        onClose={() => setEditingItem(null)}
        onSave={handleSaveItem}
        item={editingItem}
        suppliers={suppliers}
      />

      <ItemEditModal
        open={itemCreateOpen}
        onClose={() => setItemCreateOpen(false)}
        onCreate={handleCreateItem}
        item={null}
        suppliers={suppliers}
      />

      <ItemsImportModal
        open={itemsImportOpen}
        onClose={() => setItemsImportOpen(false)}
        onImport={handleImportItems}
        existingItems={items}
      />

      <SupplierEditModal
        open={!!editingSupplier}
        onClose={() => setEditingSupplier(null)}
        onSave={handleSaveSupplier}
        supplier={editingSupplier}
      />

      <SupplierEditModal
        open={supplierCreateOpen}
        onClose={() => setSupplierCreateOpen(false)}
        onCreate={handleCreateSupplier}
        supplier={null}
      />

      <StaffEditModal
        open={!!editingStaff}
        onClose={() => setEditingStaff(null)}
        onSave={handleSaveStaff}
        staff={editingStaff}
      />

      <StaffEditModal
        open={staffCreateOpen}
        onClose={() => setStaffCreateOpen(false)}
        onCreate={handleCreateStaff}
        staff={null}
      />

      <StaffImportModal
        open={staffImportOpen}
        onClose={() => setStaffImportOpen(false)}
        onImport={handleImportStaff}
        existingStaff={staff}
      />

      <SalesImportModal
        open={salesImportOpen}
        onClose={() => setSalesImportOpen(false)}
        onImport={handleImportSales}
      />

      <ItemSalesImportModal
        open={itemSalesImportOpen}
        onClose={() => setItemSalesImportOpen(false)}
        onImport={handleImportItemSales}
        existingItems={items}
      />

      <RecipeBuildModal
        open={recipeBuildOpen}
        onClose={() => setRecipeBuildOpen(false)}
        items={activeItems}
        recipes={recipes}
        createRecipe={createRecipe}
        onAccepted={({ name, count }) => {
          setToast(`Saved recipe "${name}" with ${count} ${count === 1 ? 'component' : 'components'}.`)
        }}
        onNavigateToItems={() => {
          setRecipeBuildOpen(false)
          setItemsCollapsed(false)
          requestAnimationFrame(() => {
            document.getElementById('settings-items-body')?.scrollIntoView({ behavior: 'smooth' })
          })
        }}
      />

      <RecipeLinkModal
        recipeId={linkingRecipeId}
        recipes={recipes}
        items={activeItems}
        updateRecipeItemId={updateRecipeItemId}
        onClose={() => setLinkingRecipeId(null)}
        onLinked={({ itemName }) => {
          setLinkingRecipeId(null)
          setToast(`Linked to ${itemName}`)
        }}
        onNavigateToItems={() => {
          setLinkingRecipeId(null)
          setItemsCollapsed(false)
          requestAnimationFrame(() => {
            document.getElementById('settings-items-body')?.scrollIntoView({ behavior: 'smooth' })
          })
        }}
      />

      <Toast message={toast} onDone={() => setToast('')} />
    </div>
  )
}

// Small stat tile used by the Sales Data rollup grid.
// "emphasized" gives the Today tile a bit more visual weight since
// it's the most-watched value.
function RollupTile({ label, value, hint, emphasized }) {
  return (
    <div
      className={`rounded-xl border p-2 ${
        emphasized
          ? 'border-emerald-200 bg-emerald-50/40'
          : 'border-black/10 bg-black/[0.02]'
      }`}
    >
      <div className="text-[10px] font-medium text-black/55">
        {label}
      </div>
      <div className={`mt-0.5 text-base font-medium tabular-nums ${
        emphasized ? 'text-emerald-800' : 'text-black/85'
      }`}>
        {formatMoney(Number(value || 0))}
      </div>
      {hint && (
        <div className="text-[10px] text-black/55">
          {hint}
        </div>
      )}
    </div>
  )
}
